import type { WorkflowPlan } from '../../store/chat';
import { CubeIcon, SpinnerIcon, RocketIcon, CheckIcon } from '../ui/icons';

interface PlanPreviewProps {
  plan: WorkflowPlan;
  onBuild: () => void;
  isBuilding: boolean;
  isBuilt: boolean;
}

export function PlanPreview({
  plan,
  onBuild,
  isBuilding,
  isBuilt,
}: PlanPreviewProps) {
  return (
    <div className="flex h-full flex-col min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
          <CubeIcon className="h-4 w-4 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {plan.title}
          </h3>
          <p className="text-xs text-gray-500">{plan.steps.length} steps</p>
        </div>
        {isBuilt && (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-4 w-4 text-green-600" />
          </span>
        )}
      </div>

      {/* Actions - only show if not built */}
      {!isBuilt && (
        <div className="flex-shrink-0 flex gap-2 border-b border-gray-200 p-3">
          <button
            onClick={onBuild}
            disabled={isBuilding}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBuilding ? (
              <>
                <SpinnerIcon className="h-4 w-4" />
                Building...
              </>
            ) : (
              <>
                <RocketIcon className="h-4 w-4" />
                Begin building
              </>
            )}
          </button>
        </div>
      )}

      {/* Scrollable content area - contains summary, steps, credentials, and connections */}
      <div className="flex-1 overflow-auto min-h-0">
        {/* Summary */}
        <div className="border-b border-gray-100 px-4 py-3">
          <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-400">
            Summary
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">{plan.summary}</p>
        </div>

        {/* Steps */}
        <div className="px-4 py-3">
          <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-400">
            Workflow Steps
          </h4>
          <ol className="space-y-4">
            {plan.steps.map((step, index) => (
              <li key={step.stepNumber} className="relative pl-6">
                {/* Connection line */}
                {index < plan.steps.length - 1 && (
                  <div className="absolute left-2 top-6 h-full w-px bg-gray-200" />
                )}

                {/* Step number bubble */}
                <span className="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700">
                  {step.stepNumber}
                </span>

                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {step.nodeName}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                  {step.configuration && (
                    <p className="mt-1 text-xs text-gray-400 italic">
                      {step.configuration}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Required Credentials */}
        {plan.requiredCredentials && plan.requiredCredentials.length > 0 && (
          <div className="border-t border-gray-200 px-4 py-3">
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
              Required Credentials
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {plan.requiredCredentials.map((cred, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"
                >
                  {cred}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Connections */}
        {plan.connections && (
          <div className="border-t border-gray-200 px-4 py-3">
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-400">
              Data Flow
            </h4>
            <p className="text-sm text-gray-600">{plan.connections}</p>
          </div>
        )}
      </div>
    </div>
  );
}
